<template>
  <q-layout
    class="ai-chat-layout"
    :class="{ 'ai-chat-layout--open': isOpen }"
    container
    view="lHh Lpr lFf"
  >
    <q-drawer
      class="ai-chat-panel"
      v-model="isOpen"
      side="left"
      overlay
      :width="panelWidth"
      @wheel.stop
      @touchmove.stop.prevent
    >
      <div class="ai-chat-drawer">
        <div
          class="panel-shell"
          :class="{
            'panel-shell--history-open': showHistoryRail,
            'panel-shell--mobile-history': isMobile && showMobileHistory
          }"
        >
          <aside v-if="showHistoryRail" class="history-rail">
            <div class="history-header q-pa-sm">
              <q-btn
                color="primary"
                unelevated
                icon="add_comment"
                label="New chat"
                :disable="isSending || isDeletingSession"
                @click="handleNewChat"
              />
              <q-btn
                dense
                flat
                icon="close"
                @click="closeHistoryRail"
                title="Close history"
              />
            </div>

            <q-scroll-area class="history-scroll">
              <div class="q-pa-sm column q-gutter-sm">
                <div v-if="!sessionList.length && !historyLoading" class="history-empty">
                  <div class="text-subtitle2">No saved chats yet</div>
                  <div class="text-caption text-grey-7">
                    Start a conversation and it will appear here automatically.
                  </div>
                </div>

                <q-list v-else separator>
                  <q-item
                    v-for="session in sessionList"
                    :key="session.sessionId"
                    clickable
                    class="history-session"
                    active-class="history-session--active"
                    :active="session.sessionId === activeSessionId"
                    :disable="isSending"
                    @click="selectSession(session.sessionId)"
                  >
                    <q-item-section>
                      <q-item-label lines="1">{{ session.title || 'New chat' }}</q-item-label>
                      <q-item-label caption lines="2">
                        {{ session.lastMessagePreview || 'No messages yet' }}
                      </q-item-label>
                      <q-item-label caption class="text-grey-6">
                        {{ formatSessionUpdated(session) }}
                      </q-item-label>
                    </q-item-section>
                    <q-item-section side top>
                      <q-btn
                        dense
                        flat
                        round
                        icon="delete"
                        color="negative"
                        :disable="isDeletingSession || isSending"
                        @click.stop="handleDeleteSession(session)"
                        title="Delete chat"
                      />
                    </q-item-section>
                  </q-item>
                </q-list>

                <div v-if="sessionsCursor" class="row justify-center">
                  <q-btn
                    flat
                    no-caps
                    label="Load more chats"
                    :loading="historyLoading"
                    @click="loadMoreSessions"
                  />
                </div>
              </div>
            </q-scroll-area>
          </aside>

          <section v-if="!isMobile || !showMobileHistory" class="conversation-pane">
            <div class="conversation-header q-pa-sm column q-gutter-sm">
              <div class="row items-center justify-between q-col-gutter-sm">
                <div class="col-auto row items-center q-gutter-xs no-wrap">
                  <q-btn
                    dense
                    flat
                    :icon="showHistoryRail ? 'chat' : 'history'"
                    @click="toggleHistoryRail"
                    :title="showHistoryRail ? 'Hide history' : 'Show history'"
                  />
                  <q-btn
                    dense
                    flat
                    icon="hub"
                    @click="enableMcp"
                    :disable="mcpEnabled"
                    :title="mcpEnabled ? 'WebMCP enabled' : 'Enable WebMCP'"
                  />
                  <div class="conversation-title">
                    <div class="text-subtitle2 ellipsis">{{ activeSessionTitle }}</div>
                    <div class="text-caption text-grey-7">{{ activeSessionMeta }}</div>
                  </div>
                </div>

                <div class="col-auto row items-center q-gutter-xs no-wrap">
                  <q-select
                    dense
                    borderless
                    emit-value
                    map-options
                    :options="personaOptions"
                    :model-value="store.selectedPersonaId"
                    @update:model-value="store.setPersona($event)"
                    style="min-width: 130px"
                  >
                    <template #selected-item="{ opt }">
                      <div class="row items-center q-gutter-xs no-wrap">
                        <q-avatar size="20px" round>
                          <img :src="opt.avatar" :alt="opt.displayName" />
                        </q-avatar>
                        <span class="text-caption">{{ opt.displayName }}</span>
                      </div>
                    </template>
                    <template #option="{ itemProps, opt }">
                      <q-item v-bind="itemProps">
                        <q-item-section avatar>
                          <q-avatar size="28px" round>
                            <img :src="opt.avatar" :alt="opt.displayName" />
                          </q-avatar>
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ opt.displayName }}</q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                  </q-select>
                  <q-btn
                    dense
                    flat
                    icon="close"
                    @click="store.setOpen(false)"
                    title="Close"
                    aria-label="Close AI chat panel"
                  />
                </div>
              </div>

              <template v-if="isAdmin">
                <div class="column">
                  <div class="row items-center q-col-gutter-sm">
                    <div class="col-12">
                      <q-select
                        dense
                        outlined
                        emit-value
                        map-options
                        :options="adminModelOptions"
                        v-model="selectedAdminModel"
                        class="truncate-select"
                        :display-value="adminSelectedDisplay"
                        :title="adminSelectedFullLabel"
                        label="Chat model"
                      />
                    </div>
                    <div class="col">
                      <q-toggle v-model="adminFreeModelsOnly" dense label="Free models only" />
                    </div>
                    <div class="col">
                      <q-toggle v-model="showAllTextModels" dense label="All text models" />
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="text-caption text-grey-7">
                Chat uses the system default model.
              </div>
            </div>

            <q-scroll-area
              ref="scrollArea"
              class="chat-body"
              @wheel.stop.prevent="onWheelScroll"
              @touchmove.stop
              :thumb-style="scrollThumbStyle"
              :bar-style="scrollBarStyle"
              :content-style="scrollContentStyle"
            >
              <div class="q-mx-md chat-messages q-py-sm">
                <div v-if="activeSessionId && messagesCursor" class="row justify-center q-mb-sm">
                  <q-btn
                    flat
                    no-caps
                    label="Load older messages"
                    :loading="historyLoading"
                    @click="handleLoadOlderMessages"
                  />
                </div>

                <div
                  v-if="!messages.length && !isTyping && !historyLoading"
                  class="chat-empty text-center q-pa-lg"
                >
                  <div class="text-subtitle2">Start a new chat</div>
                  <div class="text-caption text-grey-7">
                    Ask a question, upload context, or reopen an older session from history.
                  </div>
                </div>

                <q-chat-message
                  v-for="(msg, idx) in messages"
                  :key="msg.id || `${msg.role}-${idx}-${msg.stamp || 'nostamp'}`"
                  :name="msg.role === 'user' ? userName : getMsgPersona(msg).displayName"
                  :sent="msg.role === 'user'"
                  :stamp="msg.stamp || ''"
                  :avatar="getAvatar(msg, idx)"
                  text-color="black"
                >
                  <template #default>
                    <div v-if="msg.placeholder" class="typing-dots">
                      <span class="typing-dot"></span>
                      <span class="typing-dot"></span>
                      <span class="typing-dot"></span>
                    </div>

                    <div v-else-if="msg.type === 'confirm'" class="confirm-bubble">
                      <div class="confirm-title">{{ msg.title || 'Confirm' }}</div>
                      <div class="confirm-message">{{ msg.content }}</div>
                      <div class="confirm-actions q-mt-sm">
                        <q-btn
                          size="sm"
                          color="primary"
                          text-color="white"
                          label="Proceed"
                          class="q-mr-sm"
                          @click="store.resolveConfirm(msg.confirmId, true)"
                        />
                        <q-btn
                          size="sm"
                          flat
                          label="Cancel"
                          text-color="white"
                          @click="store.resolveConfirm(msg.confirmId, false)"
                        />
                      </div>
                    </div>

                    <div v-else>
                      <template v-if="msg.content">
                        <template
                          v-for="(part, pidx) in splitMessageContent(msg.content)"
                          :key="`${msg.id || idx}-part-${pidx}`"
                        >
                          <AiChatMarkdown
                            v-if="part.type === 'md'"
                            class="chat-markdown"
                            :content="part.text"
                          />
                          <q-img
                            v-else-if="part.type === 'image'"
                            :src="part.url"
                            :alt="part.alt || 'image'"
                            class="chat-inline-image q-mt-xs q-mb-xs"
                            loading="lazy"
                            no-spinner
                            fit="contain"
                            @click="openImage(part)"
                            style="cursor: zoom-in"
                          />
                        </template>
                      </template>

                      <div
                        v-if="attachmentItems(msg).length"
                        class="row items-center q-gutter-xs q-mt-sm"
                      >
                        <q-chip
                          v-for="(item, itemIndex) in attachmentItems(msg)"
                          :key="`${msg.id || idx}-attachment-${itemIndex}`"
                          dense
                          outline
                          color="primary"
                          icon="attach_file"
                        >
                          {{ attachmentLabel(item) }}
                        </q-chip>
                      </div>

                      <div
                        v-if="
                          Array.isArray(msg?.clarification?.choices) &&
                          msg.clarification.choices.length > 0
                        "
                        class="row items-center q-gutter-xs q-mt-sm"
                      >
                        <q-chip
                          v-for="(choice, cidx) in msg.clarification.choices"
                          :key="`${idx}-choice-${cidx}`"
                          clickable
                          :disable="isSending"
                          @click="submitClarificationChoice(msg, choice)"
                        >
                          {{ choice.label }}
                        </q-chip>
                      </div>
                    </div>
                  </template>

                  <template #avatar>
                    <UserAvatarBadge
                      v-if="msg.role === 'user'"
                      :photoURL="userAvatar || undefined"
                      :displayName="userName"
                      :size="'32px'"
                    />
                    <q-avatar v-else size="32px" round>
                      <img v-if="getAvatar(msg, idx)" :src="getAvatar(msg, idx)" alt="avatar" />
                      <span v-else>{{ getMsgPersona(msg).displayName[0] }}</span>
                    </q-avatar>
                  </template>
                </q-chat-message>

                <q-chat-message
                  v-if="isTyping && !hasPlaceholder"
                  :name="currentPersona.displayName"
                  :sent="false"
                  :avatar="activeAiAvatar"
                  text-color="black"
                >
                  <div class="typing-dots">
                    <span class="typing-dot"></span>
                    <span class="typing-dot"></span>
                    <span class="typing-dot"></span>
                  </div>
                </q-chat-message>
              </div>
            </q-scroll-area>

            <div class="q-pt-sm chat-footer">
              <q-input
                class="userinput"
                v-model="draft"
                type="textarea"
                autogrow
                :disable="isSending"
                :placeholder="`Ask ${currentPersona.displayName}...`"
                input-style="padding-left: 10px; padding-right: 10px;"
                @keyup.enter.exact.prevent="handleSend"
              />

              <div class="row items-center justify-between q-mt-sm">
                <div class="row items-center q-gutter-sm">
                  <input
                    ref="fileEl"
                    type="file"
                    accept="image/*,.pdf,.txt,.md,.json,.csv,text/plain,text/markdown,text/csv,application/json,application/pdf"
                    multiple
                    class="hidden"
                    @change="onFiles"
                  />
                  <q-btn
                    v-if="canAttach"
                    dense
                    flat
                    icon="attach_file"
                    :disable="isSending"
                    @click="pickAttachment"
                  />
                </div>

                <div class="row items-center q-gutter-xs">
                  <q-btn
                    v-if="isStreaming"
                    dense
                    flat
                    icon="stop"
                    color="negative"
                    title="Stop generating"
                    @click="store.cancelCurrentStream"
                  />
                  <q-spinner-dots v-if="isStreaming" size="16px" color="primary" />
                  <q-btn color="primary" :disable="!canSend" label="Send" @click="handleSend" />
                </div>
              </div>

              <div v-if="canAttach && attachments.length" class="q-mt-sm row items-center q-gutter-sm">
                <q-chip
                  v-for="(file, index) in attachments"
                  :key="`${file.name || 'attachment'}-${index}`"
                  removable
                  @remove="removeAttachment(index)"
                >
                  {{ attachmentLabel(file) }}
                </q-chip>
              </div>
            </div>
          </section>
        </div>
      </div>
    </q-drawer>
    <q-page-container />
  </q-layout>
</template>

<script setup>
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { date, useQuasar } from 'quasar'
  import { AI_PROVIDERS } from '@/config/aiConfig'
  import { ensureWebMCP, mapToolsToWebMCP } from '@/boot/webmcp'
  import { openLightbox } from '@/services/lightboxService'
  import { AI_PERSONAS, useAiChatStore } from '@/stores/aiChatStore'
  import { useUserStore } from '@/stores/userStore'
  import AiChatMarkdown from './AiChatMarkdown.vue'
  import UserAvatarBadge from '@/components/ui/badges/UserAvatarBadge.vue'

  const ADMIN_OVERRIDE_STORAGE_KEY = 'ai_chat_admin_model_override'

  const $q = useQuasar()
  const store = useAiChatStore()
  const userStore = useUserStore()

  const isOpen = computed({ get: () => store.isOpen, set: (value) => store.setOpen(value) })
  const messages = computed(() => store.messages)
  const isTyping = computed(() => store.isTyping)
  const isSending = computed(() => store.isSending)
  const attachments = computed(() => store.attachments)
  const hasPlaceholder = computed(() => messages.value.some((message) => message?.placeholder))
  const isStreaming = computed(() => !!store.currentStreamCtl)
  const isAdmin = computed(() => !!userStore?.isAdmin)
  const isPaid = computed(
    () =>
      !!(
        userStore?.subscription &&
        userStore.subscription.tier &&
        userStore.subscription.tier !== 'free'
      )
  )
  const isMobile = computed(() => !!$q?.screen?.lt?.md)
  const activeSessionId = computed(() => store.activeSessionId)
  const activeSessionSummary = computed(() => store.activeSessionSummary)
  const sessionList = computed(() => store.sessionList)
  const sessionsCursor = computed(() => store.sessionsCursor)
  const messagesCursor = computed(() => store.messagesCursor)
  const historyLoading = computed(() => store.historyLoading)
  const isDeletingSession = computed(() => store.isDeletingSession)
  const showDesktopHistory = ref(false)
  const showHistoryRail = computed(() =>
    isMobile.value ? showMobileHistory.value : showDesktopHistory.value
  )

  const BASE_PANEL_WIDTH_DESKTOP = 380
  const BASE_PANEL_WIDTH_DESKTOP_HISTORY = 940
  const BASE_PANEL_WIDTH_MOBILE = 520
  const panelWidth = computed(() => {
    const margin = 8
    const screenWidth =
      $q?.screen?.width || (typeof window !== 'undefined' ? window.innerWidth : 1024)
    const available = Math.max(0, screenWidth - margin * 2)
    const preferredWidth = isMobile.value
      ? BASE_PANEL_WIDTH_MOBILE
      : showHistoryRail.value
        ? BASE_PANEL_WIDTH_DESKTOP_HISTORY
        : BASE_PANEL_WIDTH_DESKTOP
    return Math.min(preferredWidth, available)
  })

  const draft = ref('')
  const mcpEnabled = ref(false)
  const adminFreeModelsOnly = ref(false)
  const showAllTextModels = ref(true)
  const selectedAdminModel = ref('')
  const showMobileHistory = ref(false)
  const isPinnedToBottom = ref(true)
  const suppressAutoScrollOnce = ref(false)
  const forceNextScroll = ref(false)
  const scrollArea = ref(null)
  const fileEl = ref(null)

  const userName = computed(() => userStore?.user?.displayName || 'You')
  const userAvatar = computed(() => userStore?.userAvatar || userStore?.profile?.photoURL || '')
  const currentPersona = computed(() => store.currentPersona)
  const personaOptions = computed(() =>
    store.availablePersonas.map((persona) => ({
      label: persona.displayName,
      value: persona.id,
      ...persona
    }))
  )
  const activeAiAvatar = computed(() => store.aiAvatarUrl)
  const latestAssistantMessageIndex = computed(() => {
    for (let index = messages.value.length - 1; index >= 0; index -= 1) {
      if (messages.value[index]?.role === 'assistant') return index
    }
    return -1
  })
  const canSend = computed(
    () => (draft.value.trim().length > 0 || attachments.value.length > 0) && !isSending.value
  )
  const canAttach = computed(() => isPaid.value)
  const activeSessionTitle = computed(() => activeSessionSummary.value?.title || 'New chat')
  const activeSessionMeta = computed(() => {
    if (!activeSessionId.value) return 'Unsaved draft'
    const updatedAt = activeSessionSummary.value?.updatedAt || activeSessionSummary.value?.createdAt
    if (!updatedAt) return 'Saved chat'
    return `Updated ${formatSessionTimestamp(updatedAt)}`
  })

  let scrollTarget = null

  function loadStoredAdminOverride() {
    try {
      return localStorage.getItem(ADMIN_OVERRIDE_STORAGE_KEY) || ''
    } catch {
      return ''
    }
  }

  function persistAdminOverride(value) {
    try {
      if (!value) {
        localStorage.removeItem(ADMIN_OVERRIDE_STORAGE_KEY)
        return
      }
      localStorage.setItem(ADMIN_OVERRIDE_STORAGE_KEY, value)
    } catch {
      /* ignore localStorage failures */
    }
  }

  const allAdminTextModels = computed(() => {
    const deduped = new Map()
    for (const provider of AI_PROVIDERS.filter((item) => item.enabled)) {
      for (const model of provider.models || []) {
        if (!model?.enabled || !model?.outputModalities?.text) continue
        if (deduped.has(model.id)) continue
        deduped.set(model.id, {
          ...model,
          providerId: provider.id,
          providerName: provider.name
        })
      }
    }
    return Array.from(deduped.values())
  })

  const adminModelOptions = computed(() => {
    const allowedProviderIds = showAllTextModels.value ? null : new Set(['openrouter'])
    let models = allAdminTextModels.value.filter(
      (model) => !allowedProviderIds || allowedProviderIds.has(model.providerId)
    )
    if (adminFreeModelsOnly.value) {
      models = models.filter((model) => !!model.free)
    }

    models = models.slice().sort((left, right) => {
      if (!!left.free !== !!right.free) return left.free ? -1 : 1
      if (left.providerName !== right.providerName) {
        return left.providerName.localeCompare(right.providerName)
      }
      return left.name.localeCompare(right.name)
    })

    const options = models.map((model) => ({
      label: `${model.name} [${model.providerName}]`,
      value: model.id
    }))

    if (
      selectedAdminModel.value &&
      !options.some((option) => option.value === selectedAdminModel.value)
    ) {
      const current = allAdminTextModels.value.find(
        (model) => model.id === selectedAdminModel.value
      )
      if (current) {
        options.unshift({
          label: `${current.name} [${current.providerName}]`,
          value: current.id
        })
      }
    }

    return [{ label: 'System Default', value: '' }, ...options]
  })

  function truncateMiddle(value, max = 28) {
    try {
      const text = String(value || '')
      if (text.length <= max) return text
      const half = Math.max(1, Math.floor((max - 3) / 2))
      return `${text.slice(0, half)}...${text.slice(-half)}`
    } catch {
      return String(value || '')
    }
  }

  const adminSelectedFullLabel = computed(() => {
    const current = adminModelOptions.value.find((option) => option.value === selectedAdminModel.value)
    return current?.label || 'System Default'
  })
  const adminSelectedDisplay = computed(() => truncateMiddle(adminSelectedFullLabel.value, 32))

  watch(selectedAdminModel, (value) => {
    persistAdminOverride(value || '')
  })

  watch(isAdmin, (value) => {
    if (value) {
      selectedAdminModel.value = loadStoredAdminOverride()
    } else {
      selectedAdminModel.value = ''
    }
  })

  watch(isMobile, (value) => {
    if (!value) {
      showMobileHistory.value = false
      return
    }
    showDesktopHistory.value = false
  })

  function normalizeEscapedLineBreaks(value) {
    const src = String(value || '')
    if (
      (!src.includes('\\n') && !src.includes('\\r')) ||
      src.includes('\n') ||
      src.includes('\r')
    ) {
      return src
    }
    return src
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\n')
  }

  function splitMessageContent(content) {
    const src = normalizeEscapedLineBreaks(content)
    const lines = src.split('\n')
    const parts = []
    let buffer = []
    const flushText = () => {
      if (buffer.length) {
        parts.push({ type: 'md', text: buffer.join('\n') })
        buffer = []
      }
    }
    const reKb = /^\s*(https?:\/\/\S+?)\s*:\s*(.+)\s*$/
    const reMd = /^\s*!\[(.*?)\]\((https?:\/\/[^\s)]+)\)\s*$/
    const reBare = /^\s*(https?:\/\/\S+\.(?:png|jpe?g|gif|webp|bmp|svg)(?:\?[^\s]*)?)\s*$/i
    for (const line of lines) {
      let match = line.match(reKb)
      if (match) {
        flushText()
        parts.push({ type: 'image', url: match[1], alt: match[2].trim() })
        continue
      }
      match = line.match(reMd)
      if (match) {
        flushText()
        parts.push({ type: 'image', url: match[2], alt: match[1] || '' })
        continue
      }
      match = line.match(reBare)
      if (match) {
        flushText()
        parts.push({ type: 'image', url: match[1], alt: '' })
        continue
      }
      buffer.push(line)
    }
    flushText()
    return parts.length ? parts : [{ type: 'md', text: src }]
  }

  function attachmentItems(message = {}) {
    const items = Array.isArray(message?.metadata?.attachments?.items)
      ? message.metadata.attachments.items
      : []
    return items
      .map((item) => ({
        name: String(item?.name || '').trim() || null,
        type: String(item?.type || '').trim() || null,
        size: Number(item?.size || 0)
      }))
      .filter((item) => item.name || item.type || item.size > 0)
  }

  function attachmentLabel(item = {}) {
    const name = String(item?.name || '').trim()
    if (name) return name
    const type = String(item?.type || '').trim()
    if (type) return type
    return 'attachment'
  }

  const getMsgPersona = (message) => {
    if (message?.personaId && AI_PERSONAS[message.personaId]) return AI_PERSONAS[message.personaId]
    const metadataPersonaId = String(message?.metadata?.personaId || '').trim()
    if (metadataPersonaId && AI_PERSONAS[metadataPersonaId]) return AI_PERSONAS[metadataPersonaId]
    return currentPersona.value
  }

  const getAvatar = (message, index) => {
    if (message?.role === 'user') return userAvatar.value || undefined
    const msgPersona = getMsgPersona(message)
    if (message?.role !== 'assistant') return activeAiAvatar.value || msgPersona.avatar || undefined

    const isActiveAssistant =
      index === latestAssistantMessageIndex.value && (isTyping.value || isStreaming.value)
    return (isActiveAssistant ? activeAiAvatar.value : msgPersona.avatar) || undefined
  }

  const scrollThumbStyle = {
    right: '2px',
    width: '8px',
    borderRadius: '6px',
    background: 'rgba(255, 255, 255, 0.45)',
    opacity: 0.9
  }

  const scrollBarStyle = {
    right: '0px',
    width: '10px',
    borderRadius: '6px',
    background: 'rgba(255, 255, 255, 0.08)',
    opacity: 0.7
  }

  const scrollContentStyle = {
    overscrollBehavior: 'contain',
    maxWidth: '100%'
  }

  function formatSessionTimestamp(value) {
    const parsed = Date.parse(String(value || ''))
    if (!Number.isFinite(parsed)) return 'recently'
    const now = new Date()
    const target = new Date(parsed)
    const sameDay =
      now.getFullYear() === target.getFullYear() &&
      now.getMonth() === target.getMonth() &&
      now.getDate() === target.getDate()
    return sameDay ? date.formatDate(parsed, 'HH:mm') : date.formatDate(parsed, 'MMM D')
  }

  function formatSessionUpdated(session = {}) {
    const updatedAt = session?.updatedAt || session?.createdAt
    return updatedAt ? formatSessionTimestamp(updatedAt) : 'recently'
  }

  function updatePinnedState() {
    const target = scrollArea.value?.getScrollTarget?.()
    if (!target) return
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    isPinnedToBottom.value = distanceFromBottom <= 56
  }

  function onScrollTarget() {
    updatePinnedState()
  }

  function detachScrollTarget() {
    if (scrollTarget) {
      scrollTarget.removeEventListener('scroll', onScrollTarget)
      scrollTarget = null
    }
  }

  async function bindScrollTarget() {
    await nextTick()
    const target = scrollArea.value?.getScrollTarget?.()
    if (!target || target === scrollTarget) return
    detachScrollTarget()
    scrollTarget = target
    scrollTarget.addEventListener('scroll', onScrollTarget, { passive: true })
    updatePinnedState()
  }

  const onWheelScroll = (event) => {
    if (!scrollArea.value) return
    const target = scrollArea.value.getScrollTarget()
    if (!target) return
    event.preventDefault()
    target.scrollTop += event.deltaY
  }

  const scrollToBottom = async (force = false) => {
    await bindScrollTarget()
    await nextTick()
    const el = scrollArea.value
    if (!el || typeof el.setScrollPosition !== 'function') return
    if (!force && !isPinnedToBottom.value) return
    el.setScrollPosition('vertical', Number.MAX_SAFE_INTEGER, 0)
    isPinnedToBottom.value = true
  }

  watch(
    messages,
    async () => {
      await bindScrollTarget()
      if (suppressAutoScrollOnce.value) {
        suppressAutoScrollOnce.value = false
        return
      }
      const shouldForce = forceNextScroll.value
      forceNextScroll.value = false
      if (shouldForce || isPinnedToBottom.value) {
        await scrollToBottom(true)
      }
    },
    { deep: true }
  )

  watch(
    () => isOpen.value,
    async (value) => {
      if (!value) return
      await bindScrollTarget()
      await scrollToBottom(true)
    }
  )

  onMounted(async () => {
    store.loadPersonaFromStorage()
    if (isAdmin.value) {
      selectedAdminModel.value = loadStoredAdminOverride()
    }
    forceNextScroll.value = true
    await bindScrollTarget()
    await scrollToBottom(true)
  })

  onUnmounted(() => {
    detachScrollTarget()
  })

  function handleSend() {
    if (!canSend.value) return
    const text = draft.value
    draft.value = ''
    showMobileHistory.value = false
    store.sendUserMessage(text, {
      modelOverride: isAdmin.value ? selectedAdminModel.value || null : null
    })
  }

  async function handleNewChat() {
    store.startNewChat()
    draft.value = ''
    showMobileHistory.value = false
    showDesktopHistory.value = false
    forceNextScroll.value = true
    await nextTick()
    await scrollToBottom(true)
  }

  async function selectSession(sessionId) {
    if (!sessionId || isSending.value) return
    await store.openSession(sessionId)
    closeHistoryRail()
    forceNextScroll.value = true
    await scrollToBottom(true)
  }

  async function loadMoreSessions() {
    await store.loadSessionList()
  }

  async function handleLoadOlderMessages() {
    suppressAutoScrollOnce.value = true
    await store.loadOlderMessages()
    await bindScrollTarget()
  }

  async function confirmDeleteSession(session = null) {
    const title = String(session?.title || 'this chat').trim() || 'this chat'
    return await new Promise((resolve) => {
      $q.dialog({
        title: 'Delete chat?',
        message: `Delete "${title}" from your saved AI chats?`,
        cancel: true,
        persistent: true,
        ok: {
          label: 'Delete',
          color: 'negative',
          flat: false
        }
      })
        .onOk(() => resolve(true))
        .onCancel(() => resolve(false))
        .onDismiss(() => resolve(false))
    })
  }

  async function handleDeleteSession(session = null) {
    if (!session?.sessionId || isDeletingSession.value) return
    const approved = await confirmDeleteSession(session)
    if (!approved) return
    await store.deleteSession(session.sessionId)
    if (isMobile.value && !store.activeSessionId) {
      showMobileHistory.value = sessionList.value.length > 0
    }
  }

  function closeHistoryRail() {
    showMobileHistory.value = false
    showDesktopHistory.value = false
  }

  function toggleHistoryRail() {
    if (isMobile.value) {
      showMobileHistory.value = !showMobileHistory.value
    } else {
      showDesktopHistory.value = !showDesktopHistory.value
    }
    if (!(isMobile.value ? showMobileHistory.value : showDesktopHistory.value)) {
      void bindScrollTarget().then(() => scrollToBottom(false))
    }
  }

  function buildClarificationChoiceMessage(message, choice) {
    const value = String(choice?.value || choice?.label || '').trim()
    if (!value) return ''
    const entityType = String(choice?.entityType || '')
      .trim()
      .toLowerCase()
    const vaultName = String(choice?.vaultName || '').trim()
    if (entityType === 'vault') {
      return `Use vault "${value}".`
    }
    if (entityType === 'note' && vaultName) {
      return `Use note "${value}" in vault "${vaultName}".`
    }
    if (entityType === 'note') {
      return `Use note "${value}".`
    }
    if (entityType === 'character') {
      return `Use character "${value}".`
    }
    if (entityType === 'monster' || entityType === 'spell' || entityType === 'class') {
      return `Use ${entityType} "${value}".`
    }
    const kind = String(message?.clarification?.kind || '')
      .trim()
      .toLowerCase()
    if (kind === 'vault_choice' || kind === 'vault_suggestion') {
      return `Use vault "${value}".`
    }
    return value
  }

  function submitClarificationChoice(message, choice) {
    if (isSending.value) return
    const clarificationId = String(message?.clarification?.clarificationId || '').trim()
    const text = clarificationId
      ? String(choice?.label || choice?.value || '').trim()
      : buildClarificationChoiceMessage(message, choice)
    if (!text) return
    store.sendUserMessage(text, {
      clarificationResolution: clarificationId
        ? {
            clarificationId,
            choiceId: String(choice?.id || '').trim() || null,
            choiceValue: String(choice?.value || choice?.label || '').trim() || null,
            choiceLabel: String(choice?.label || choice?.value || '').trim() || null
          }
        : null,
      modelOverride: isAdmin.value ? selectedAdminModel.value || null : null
    })
  }

  async function confirmWrite(payload = {}) {
    const toolName = String(payload?.name || 'tool').trim() || 'tool'
    return await new Promise((resolve) => {
      $q.dialog({
        title: 'Confirm write tool',
        message: `Allow "${toolName}" to modify app data from AI chat?`,
        cancel: true,
        persistent: true
      })
        .onOk(() => resolve(true))
        .onCancel(() => resolve(false))
        .onDismiss(() => resolve(false))
    })
  }

  function enableMcp() {
    try {
      const start = async () => {
        const ready = await ensureWebMCP()
        if (!ready || !window || !window.WebMCP) {
          $q.notify({
            type: 'warning',
            message: 'WebMCP script not found. Set VITE_WEBMCP_URL or place /webmcp.js'
          })
          return
        }
        const mcp = new window.WebMCP({})
        mapToolsToWebMCP(mcp, { confirmWrite })
        mcpEnabled.value = true
        $q.notify({ type: 'positive', message: 'WebMCP enabled for this page' })
      }
      void start()
    } catch {
      $q.notify({ type: 'negative', message: 'Failed to enable WebMCP' })
    }
  }

  async function openImage(part) {
    try {
      const src = part?.url || ''
      if (!src) return
      const title = part?.alt || 'image'
      await openLightbox([{ src, title }], 0)
    } catch (error) {
      console.warn('[AiChat] openImage lightbox failed', error)
    }
  }

  function pickAttachment() {
    const el = fileEl.value
    if (el) el.click()
  }

  async function onFiles(event) {
    const files = Array.from(event?.target?.files || [])
    try {
      const normalized = await Promise.all(
        files.map(async (file) => {
          try {
            return {
              name: file.name,
              type: file.type || 'application/octet-stream',
              size: file.size,
              data: await toBase64(file)
            }
          } catch (error) {
            console.warn('[AiChat] attachment encoding failed', file?.name, error)
            return null
          }
        })
      )
      for (const file of normalized.filter(Boolean)) {
        store.addAttachment(file)
      }
    } finally {
      if (fileEl.value) fileEl.value.value = ''
    }
  }

  function removeAttachment(index) {
    store.removeAttachment(index)
  }

  function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = String(reader.result || '')
        const base64 = result.includes(',') ? result.split(',')[1] : result
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/rpg/variables';

  .ai-chat-panel {
    max-width: 100%;
    overflow: hidden;
  }

  .userinput :deep(.q-field__native),
  .userinput :deep(textarea) {
    padding-left: 10px;
    padding-right: 10px;
  }

  .ai-chat-layout {
    position: fixed;
    inset: 0;
    z-index: 4000;
    pointer-events: none;
  }

  .ai-chat-layout--open {
    pointer-events: auto;
  }

  .ai-chat-drawer {
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    word-break: break-word;
    overflow-wrap: anywhere;
    @include diagonal-pattern();

    &::before {
      z-index: 0;
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }

  .panel-shell {
    flex: 1 1 auto;
    display: flex;
    min-height: 0;
    height: 100%;
    width: 100%;
  }

  .history-rail {
    width: 280px;
    min-width: 280px;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.28);
    backdrop-filter: blur(8px);
  }

  .history-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .history-scroll {
    flex: 1;
    min-height: 0;
  }

  .history-empty {
    border: 1px dashed rgba(0, 0, 0, 0.18);
    border-radius: 14px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.38);
  }

  .history-session {
    border-radius: 12px;
    margin-bottom: 4px;
  }

  .history-session--active {
    background: rgba(255, 255, 255, 0.62);
  }

  .conversation-pane {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .conversation-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(6px);
  }

  .conversation-title {
    min-width: 0;
  }

  .chat-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overscroll-behavior: contain;
    position: relative;
    pointer-events: auto;
    z-index: 0;
    max-width: 100%;
  }

  .chat-messages {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 100%;
    justify-content: flex-end;
    max-width: 100%;
  }

  .chat-empty {
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.34);
  }

  .chat-markdown {
    color: inherit;
    font-size: 15px;
    line-height: 1.6;
  }

  .chat-markdown :deep(img) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 6px 0;
    border-radius: 6px;
  }

  .chat-inline-image {
    width: 100%;
    max-height: 320px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.04);
  }

  .typing-dots {
    display: inline-flex;
    align-items: center;
  }

  .typing-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-right: 4px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.65);
    animation: typing-bounce 1.2s infinite ease-in-out;
  }

  .typing-dot:last-child {
    margin-right: 0;
  }

  .typing-dot:nth-child(2) {
    animation-delay: 0.15s;
  }

  .typing-dot:nth-child(3) {
    animation-delay: 0.3s;
  }

  @keyframes typing-bounce {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.5;
    }

    30% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }

  .chat-footer {
    margin-top: auto;
    position: relative;
    z-index: 5;
    pointer-events: auto;
    background: transparent;
  }

  .confirm-bubble {
    max-width: 100%;
  }

  .confirm-title {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .confirm-message {
    font-size: 14px;
  }

  .truncate-select :deep(.q-field__native),
  .truncate-select :deep(.q-field__input) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 1023px) {
    .panel-shell {
      display: block;
    }

    .history-rail {
      width: 100%;
      min-width: 0;
      height: 100%;
      border-right: 0;
    }

    .panel-shell--mobile-history .conversation-pane {
      display: none;
    }
  }
</style>

<style lang="scss">
  @import '@/assets/styles/rpg/variables';

  body.body--dark .ai-chat-drawer {
    @include dark-diagonal-pattern();
    position: relative;
  }

  body.body--dark .ai-chat-drawer::before {
    z-index: 0;
    pointer-events: none;
  }

  body.body--dark .ai-chat-drawer > * {
    position: relative;
    z-index: 1;
  }

  body.body--dark .ai-chat-drawer .q-message-text--received {
    color: #4d5561;
  }

  body.body--dark .ai-chat-drawer .q-message-text-content--received {
    color: #f3f5f7 !important;
  }

  body.body--dark .ai-chat-drawer .q-message-text--sent {
    color: #616a77;
  }

  body.body--dark .ai-chat-drawer .q-message-text-content--sent {
    color: #f8fafc !important;
  }

  body.body--dark .ai-chat-drawer .chat-markdown {
    color: inherit;
  }

  body.body--dark .ai-chat-drawer .q-message-stamp {
    color: rgba(248, 250, 252, 0.75);
  }

  body.body--dark .ai-chat-drawer .history-rail,
  body.body--dark .ai-chat-drawer .conversation-header,
  body.body--dark .ai-chat-drawer .chat-empty,
  body.body--dark .ai-chat-drawer .history-empty,
  body.body--dark .ai-chat-drawer .history-session--active {
    background: rgba(15, 23, 42, 0.36);
    border-color: rgba(255, 255, 255, 0.08);
  }

  .ai-chat-drawer .chat-markdown :is(p, ul, ol, blockquote, pre, table) {
    margin: 0.4em 0;
  }

  .ai-chat-drawer .chat-markdown ul,
  .ai-chat-drawer .chat-markdown ol {
    padding-left: 1.25rem;
  }

  .ai-chat-drawer .chat-markdown li + li {
    margin-top: 0.2em;
  }

  .ai-chat-drawer .chat-markdown table {
    width: 100%;
    border-collapse: collapse;
  }

  .ai-chat-drawer .chat-markdown a.kb-wikilink {
    text-decoration: none;
    border-bottom: 1px dashed currentColor;
  }
</style>
